import { useWarmTransferState } from 'Modules/softphone/hooks';
// Imported from its own path, not the barrel: the barrel is partially mocked in
// a dozen suites, and a focus rescue is not something to switch off by accident.
import { useOrphanedFocusRescue } from 'Modules/softphone/hooks/useOrphanedFocusRescue';
import { useSoftphoneStore } from 'Modules/softphone/store';
import React, { useRef } from 'react';
import { cn } from '~/lib/utils';
import { SoftphoneConnectionLossOverlay } from '../../SoftphoneConnectionLossOverlay';
import { SoftphoneConnectionStatus } from '../../SoftphoneConnectionStatus';
import { SoftphoneDialpad } from '../../SoftphoneDialpad';
import { SoftphoneKeypad } from '../../SoftphoneKeypad';
import {
  avatarVariants,
  connectionStatusVariants,
  keypadGridVariants,
} from '../index.styles';
import { SoftphoneCallAction } from '../SoftphoneCallAction';
import { SoftphoneCallStatus } from '../SoftphoneCallStatus';
import { SoftphoneHeader } from '../SoftphoneHeader';
import { SoftphoneOnCallActions } from '../SoftphoneOnCallActions';
import { SoftphoneOnCallViewProps } from './index.types';

/**
 * SoftphoneOnCallView - Renders the main on-call interface
 *
 * Displays the connection status, header, call status, on-call actions,
 * dialpad/keypad overlays, and the call action button.
 */
export const SoftphoneOnCallView = ({
  variant,
  testID,
  connectionQuality,
  isDialpadActive,
  isKeypadActive,
}: SoftphoneOnCallViewProps): React.ReactElement => {
  const hasAnyPadActive = isKeypadActive || isDialpadActive;

  const { isWarmTransferConsultationActive } = useWarmTransferState();

  const isConnectionLossFeedbackActive = useSoftphoneStore(
    (state) => state.isConnectionLossFeedbackActive
  );

  // Accepting a call unmounts the banner that accepted it, so focus can arrive
  // here with nowhere to be. Only then does it land on the identity group.
  //
  // Re-run on the connection flag: this section is hidden while the overlay is
  // up, so focus has to be claimed back when the overlay goes.
  const callInformationRef = useRef<HTMLElement | null>(null);
  useOrphanedFocusRescue(callInformationRef, isConnectionLossFeedbackActive);

  return (
    <>
      {/* Keep SoftphoneHeader and ConnectionStatus always mounted */}
      <div className={cn('w-full', { hidden: hasAnyPadActive })}>
        <div className={cn({ invisible: isConnectionLossFeedbackActive })}>
          <div className={connectionStatusVariants({ variant })}>
            <SoftphoneConnectionStatus
              status={connectionQuality}
              testID="softphone"
            />
          </div>

          {/*
            One region landmark for contact + status. `tabIndex={-1}` makes it a
            focus target without adding a tab stop, so the outline it can never
            show by keyboard is dropped.
          */}
          <section
            ref={callInformationRef}
            aria-label="Call information"
            tabIndex={-1}
            className="focus:outline-none"
          >
            <SoftphoneHeader
              avatarSize={avatarVariants({ variant })}
              testID={testID}
            />

            <SoftphoneCallStatus testID={testID} />
          </section>
        </div>

        {/* Actions remain visible on connection loss — buttons are individually disabled */}
        <SoftphoneOnCallActions testID={testID} />
      </div>

      {isDialpadActive && (
        <SoftphoneDialpad
          testID={testID}
          gridClassName={keypadGridVariants({ variant })}
        />
      )}

      {isKeypadActive && (
        <SoftphoneKeypad
          testID={testID}
          gridClassName={keypadGridVariants({ variant })}
        />
      )}

      {isConnectionLossFeedbackActive && <SoftphoneConnectionLossOverlay />}

      {!isWarmTransferConsultationActive && (
        <SoftphoneCallAction testID={testID} />
      )}
    </>
  );
};
